
import trimMessageForReward from '@/lib/trimMessageForReward';
import { mapState, mapGetters, mapActions } from 'vuex';
import ReviewFormFieldType from '@/enums/ReviewFormFieldType';
import ReviewOptionFieldType from '@/enums/ReviewOptionFieldType';
import ProductInfo from './ProductInfo';
import ReviewForm from './ReviewForm';
import ReviewFormConsentItemsSection from './ReviewFormConsentItemsSection';
import RewardSummary from './RewardSummary';
import RewardDialog from './RewardDialog';

const fieldKey = field => {
  switch (field.field_type) {
    case ReviewFormFieldType.REVIEW_RATING:
      return 'score';
    case ReviewFormFieldType.REVIEW_MESSAGE:
      return 'message';
    case ReviewFormFieldType.REVIEW_OPTION:
      return `option_${field.option_type.id}`;
    default:
      throw `Invalid field_type ${field.field_type}`;
  }
};

const MEDIA_IN_PROGRESS_STATUSES = ['uploading', 'creating', 'analyzing'];

export default {
  i18nComponentKey: 'ReviewsNew.components.ReviewFormView',
  name: 'ReviewFormView',
  components: {
    ProductInfo,
    ReviewForm,
    ReviewFormConsentItemsSection,
    RewardSummary
  },
  props: {
    review: { type: Object, required: true },
    product: { type: Object, required: true },
    subOrder: { type: Object, default: null },
    reviewForm: { type: Object, required: true },
    isPreview: { type: Boolean, required: true },
    rewardableItems: { type: Array, required: true }
  },
  data() {
    return {
      isSubmitting: false,
      step: null,
      isRewardSummaryStuck: false,
      rewardSummaryIO: null,
      checkedConsentIds: []
    };
  },
  computed: {
    ...mapState(['api']),
    ...mapState('reviewSettings', ['reviewSettings']),
    ...mapState('form', ['formValues']),
    ...mapState('window', ['windowService']),
    ...mapGetters('form', ['hasErrors']),
    ...mapGetters('reviewSettings', ['isVideoUploadable']),
    ...mapGetters('session', ['isLoggedIn']),
    isEditing() {
      return !!this.review.id;
    },
    hasConsentItems() {
      return !!this.reviewForm.consent_items?.length;
    },
    hasUncheckedRequiredConsent() {
      if (this.isEditing) return false;
      return (this.reviewForm.consent_items || []).some(
        item => item.required && !this.checkedConsentIds.includes(item.id)
      );
    },
    noticeButtonLabel() {
      return this.reviewForm.notice_button_label.replace(
        /%{mileage_type_point_text}/g,
        this.reviewSettings.mileage_type_point_text
      );
    },
    isMediaUploading() {
      return (this.formValues.media || []).some(e =>
        MEDIA_IN_PROGRESS_STATUSES.includes(e.mediaStatus)
      );
    },
    formFields() {
      const fields = [];
      if (!this.isLoggedIn) {
        fields.push({
          step: 1,
          key: 'user_name',
          type: 'text',
          label: this.$t('user_name_label'),
          required: true,
          inputProps: {
            placeholder: this.$t('user_name_placeholder')
          }
        });

        if (!this.isEditing) {
          fields.push({
            step: 1,
            key: 'password',
            type: 'password',
            label: this.$t('password_label'),
            required: true,
            inputProps: {
              placeholder: this.$t('password_placeholder'),
              size: 'large',
              maxlength: 64
            }
          });
        }
      }

      this.reviewForm.form_fields.forEach(e => {
        const key = fieldKey(e);
        fields.push({
          step: e.step,
          key,
          type: 'custom',
          label: e.name,
          required: e.required,
          field_config: e.field_config,
          review_reward_config: e.review_reward_config,
          option_type: e.option_type,
          inputProps: e.inputProps
        });

        if (key === 'message')
          fields.push({ step: e.step, key: 'media', type: 'custom' });
      });

      return fields;
    },
    stepFormFields() {
      return this.reviewForm.enable_multi_step
        ? this.formFields.filter(e => e.step === this.step)
        : this.formFields;
    },
    messageField() {
      return this.reviewForm.form_fields.find(
        e => e.field_type === ReviewFormFieldType.REVIEW_MESSAGE
      );
    },
    shortReviewConfig() {
      const field = this.messageField;
      if (!field) return { policy: 'none', length: 0 };

      const segments = field.review_reward_config.reward_segments;
      if (segments.length < 2) return { policy: 'none', length: 0 };

      return {
        policy: field.field_config.short_review_policy,
        length: segments[segments.length - 2].min_message_length
      };
    },
    hasConfiguredMessageReward() {
      const segments =
        this.messageField?.review_reward_config?.reward_segments || [];
      return segments.some(e => (e.base_value || 0) > 0);
    },
    stepValidationRules() {
      const profanityList = this.reviewSettings.profanity_filters;
      const noProfanityValidator = value => {
        if (!value) return true;

        const matched = profanityList.filter(e => value.includes(e));
        return matched.length > 0
          ? this.$t('no_profanity', [matched.join(', ')])
          : true;
      };

      const noSpecialCharacterValidator = value => {
        const pattern = /[^0-9ㄱ-ㅎㅏ-ㅣ가-힣a-zA-Zぁ-ゔァ-ヴー々〆〤。一-龥，\s\n\r\t!@#$%^&*()_+\-=[\]{}?/.,><₩~\\|;:'"`]/g;
        return pattern.test(value) ? this.$t('no_special_character') : true;
      };

      const mediaRequiredValidator = value =>
        value.length > 0
          ? true
          : this.isVideoUploadable
          ? this.$t('media_required')
          : this.$t('photo_required');

      const minLengthValidator = () =>
        this.validMessageLength >= this.shortReviewConfig.length ||
        this.$t('message_too_short', [this.shortReviewConfig.length]);

      return Object.fromEntries(
        this.stepFormFields.map(field => {
          const rules = {};
          if (field.required) rules.required = true;
          if (
            field.key === 'message' ||
            field.option_type?.field_type === ReviewOptionFieldType.TEXT
          ) {
            rules.noProfanity = noProfanityValidator;
            if (this.reviewSettings.allow_only_basic_chars_for_new_review)
              rules.noSpecialCharacter = noSpecialCharacterValidator;
            if (
              field.key === 'message' &&
              this.shortReviewConfig.policy === 'block'
            )
              rules.minLength = minLengthValidator;
          } else if (field.key === 'media') {
            if (this.reviewSettings.require_photo)
              rules.mediaRequired = mediaRequiredValidator;
          }
          return [field.key, rules];
        })
      );
    },
    fieldRewards() {
      const rewards = {};

      this.reviewForm.form_fields.forEach(field => {
        const key = fieldKey(field);
        if (key === 'message') {
          rewards.message = this.messageReward;
        } else if (key === 'score') {
          const maxValue =
            this.rewardableItems.find(e => e.key === 'rating')?.value || 0;
          rewards[key] = {
            currentValue: (this.formValues[key] ?? '') !== '' ? maxValue : 0,
            maxValue
          };
        } else {
          const maxValue = this.rewardableItems.some(e => e.key === 'option')
            ? field.review_reward_config.value
            : 0;
          rewards[key] = {
            currentValue: (this.formValues[key] ?? '') !== '' ? maxValue : 0,
            maxValue
          };
        }
      });

      return rewards;
    },
    messageReward() {
      const field = this.messageField;
      const segments = field.review_reward_config.reward_segments;
      const currentSegment = segments.find(
        e => e.min_message_length <= this.validMessageLength
      );

      const maxTextValue =
        this.rewardableItems.find(e => e.key === 'message')?.value || 0;
      const text = {
        currentValue: Math.min(currentSegment.base_value, maxTextValue),
        maxValue: maxTextValue
      };

      const photoMaxValue =
        this.rewardableItems.find(e => e.key === 'photo')?.value || 0;
      const photo = {
        currentValue: this.formValues.media?.length
          ? Math.min(currentSegment.photo_value, photoMaxValue)
          : 0,
        maxValue: photoMaxValue
      };

      const videoMaxValue =
        this.rewardableItems.find(e => e.key === 'video')?.value || 0;
      const video = {
        currentValue: this.formValues.media?.some(e => e.mediaType === 'video')
          ? Math.min(currentSegment.video_value, videoMaxValue)
          : 0,
        maxValue: videoMaxValue
      };

      const aiKeyword = (() => {
        if (!field.field_config.enable_ai_keyword)
          return { currentValue: 0, maxValue: 0, currentCount: 0, maxCount: 0 };

        const currentCount = this.formValues.matched_ai_keywords?.length || 0;
        const maxCount = field.field_config.ai_keywords?.length || 0;
        const maxValue =
          this.rewardableItems.find(e => e.key === 'ai_keyword')?.value || 0;
        return {
          currentValue: Math.min(
            currentCount * this.reviewSettings.ai_keyword_reward_cents,
            maxValue
          ),
          maxValue,
          currentCount,
          maxCount
        };
      })();

      const aiClothing = (() => {
        if (!field.field_config.enable_ai_clothing_detection) {
          return { currentValue: 0, maxValue: 0 };
        }
        const maxValue =
          this.rewardableItems.find(e => e.key === 'ai_clothing')?.value || 0;
        return {
          currentValue: this.formValues.media?.some(e => e.aiClothingDetected)
            ? maxValue
            : 0,
          maxValue
        };
      })();

      return {
        currentValue:
          text.currentValue +
          photo.currentValue +
          video.currentValue +
          aiKeyword.currentValue +
          aiClothing.currentValue,
        maxValue:
          text.maxValue +
          photo.maxValue +
          video.maxValue +
          aiKeyword.maxValue +
          aiClothing.maxValue,
        text,
        photo,
        video,
        aiKeyword,
        aiClothing,
        currentMessageLength: this.validMessageLength,
        maxRewardMessageLength: segments[0].min_message_length
      };
    },
    isRewardableReview() {
      return this.isLoggedIn && !this.isEditing && this.maxReward > 0;
    },
    isRewardEligible() {
      return this.missingRequiredFields.length === 0;
    },
    rewardItems() {
      if (!this.isRewardableReview) return [];

      const items = [];
      const formFields = this.reviewForm.form_fields;
      formFields.forEach(field => {
        const key = fieldKey(field);
        const reward = this.fieldRewards[key];
        switch (key) {
          case 'score':
            items.push({
              name: this.$t('score_review'),
              value: reward.currentValue,
              max: reward.maxValue
            });
            break;
          case 'message': {
            const reason = this.$t('text_review_reason', {
              current: reward.currentMessageLength,
              max: reward.maxRewardMessageLength
            });

            items.push({
              name: this.$t('text_review'),
              reason,
              value: reward.text.currentValue,
              max: reward.text.maxValue
            });
            items.push({
              name: this.$t('photo_reward'),
              reason,
              value: reward.photo.currentValue,
              max: reward.photo.maxValue
            });
            items.push({
              name: this.$t('video_reward'),
              reason,
              value: reward.video.currentValue,
              max: reward.video.maxValue
            });
            items.push({
              name: this.$t('ai_keyword'),
              reason: this.$t('ai_keyword_reason', {
                current: reward.aiKeyword.currentCount,
                max: reward.aiKeyword.maxCount
              }),
              value: reward.aiKeyword.currentValue,
              max: reward.aiKeyword.maxValue
            });
            items.push({
              name: this.$t('ai_clothing_detection'),
              value: reward.aiClothing.currentValue,
              max: reward.aiClothing.maxValue
            });
            break;
          }
          default:
            items.push({
              name: this.$t('field_name', [field.name]),
              value: reward.currentValue,
              max: reward.maxValue
            });
            break;
        }
      });

      const bonusRewardKeys = [
        'nth_step_review',
        'nth_photo_review',
        'nth_review',
        'product_price',
        'user_grade',
        'external_link',
        'first_review_per_order'
      ];
      this.rewardableItems.forEach(item => {
        if (!bonusRewardKeys.includes(item.key)) return;

        const value = (() => {
          if (item.key === 'external_link') {
            return this.formValues.message?.includes('http') ? item.value : 0;
          }
          if (item.key === 'product_price') {
            if (this.formValues.media?.length > 0) return item.media_value;
            if (this.validMessageLength > 0) return item.text_value;
            return 0;
          }
          return item.value;
        })();

        items.push({
          name: this.$t(`bonus_reward.${item.key}`),
          value,
          max: item.key === 'product_price' ? item.media_value : item.value
        });
      });

      const limitedItem = this.rewardableItems.find(e => e.key === 'limited');
      if (limitedItem) {
        const totalValue = items.reduce((a, e) => a + e.value, 0);
        const totalMax = items.reduce((a, e) => a + e.max, 0);
        items.push({
          name: this.$t('limited_reward'),
          value: totalValue <= this.maxReward ? 0 : this.maxReward - totalValue,
          max: totalMax <= this.maxReward ? 0 : this.maxReward - totalMax
        });
      }

      return items.filter(e => e.max !== 0);
    },
    currentReward() {
      return this.rewardItems.reduce((a, e) => a + e.value, 0);
    },
    maxReward() {
      return this.rewardableItems.reduce(
        (a, e) => a + (e.key === 'product_price' ? e.media_value : e.value),
        0
      );
    },
    missingRequiredFields() {
      return this.reviewForm.form_fields
        .filter(e => e.option_type?.required_for_mileage)
        .filter(e => !this.formValues[fieldKey(e)])
        .map(e => e.name);
    },
    validMessageLength() {
      const { message } = this.formValues;
      if (!message) return 0;

      return this.reviewSettings.auto_mileage_use_valid_review_message_length
        ? trimMessageForReward(message).length
        : message.length;
    }
  },
  watch: {
    isLoggedIn() {
      if (!this.isPreview) return;
      this.initializeForm();
    },
    reviewForm: {
      handler: 'initializeForm',
      immediate: true
    }
  },
  updated() {
    this.$nextTick(() => {
      this.rewardSummaryIO && this.rewardSummaryIO.disconnect();

      const root = this.$refs.body;
      const sentinel = this.$refs.rewardSummarySentinel;
      if (!root || !sentinel) return;

      this.rewardSummaryIO = new IntersectionObserver(
        ([entry]) => (this.isRewardSummaryStuck = !entry.isIntersecting),
        { root, threshold: 0 }
      );

      this.rewardSummaryIO.observe(sentinel);
    });
  },
  beforeDestroy() {
    this.rewardSummaryIO && this.rewardSummaryIO.disconnect();
  },
  methods: {
    ...mapActions('form', [
      'initFormValues',
      'setValidationRules',
      'validateAll'
    ]),
    ...mapActions('bottomSheet', ['openBottomSheet']),
    initializeForm() {
      this.initializeFormValues();
      this.setStep(this.step || 1);
    },
    initializeFormValues() {
      const formValues = {};

      this.formFields.forEach(field => {
        const value = this.review[field.key];
        if (field.key === 'score') {
          const queryScore = this.$route.query.score;
          formValues.score = queryScore ? parseInt(queryScore) : value;
        } else if (field.key === 'media') {
          formValues.media = [
            ...this.review.videos.map(e => ({
              mediaType: 'video',
              mediaStatus: 'ready',
              previewUrl: e.thumbnail_url,
              id: e.id
            })),
            ...this.review.images.map(e => ({
              mediaType: 'image',
              mediaStatus: 'ready',
              previewUrl: e.thumbnail_url,
              url: e.url,
              file: '',
              aiClothingDetected: e.ai_clothing_detected
            }))
          ];
        } else if (field.option_type) {
          const optionValue = this.review.options.find(
            e => e.review_option_type_id === field.option_type.id
          )?.value;
          if (field.option_type.field_type === ReviewOptionFieldType.SELECT) {
            const options = field.option_type.field_options.options || [];
            const isValid =
              optionValue != null &&
              options.some(option => option.value === optionValue);
            formValues[field.key] = isValid ? optionValue : null;
          } else {
            formValues[field.key] = optionValue ?? null;
          }
        } else if (value) formValues[field.key] = value;
      });

      if (this.review.matched_ai_keywords?.length)
        formValues.matched_ai_keywords = this.review.matched_ai_keywords;

      this.checkedConsentIds = [...(this.review.consent_preset_ids || [])];
      this.initFormValues(formValues);
    },
    setStep(step) {
      this.step = step;
      this.setValidationRules(this.stepValidationRules);
    },
    onClickPrevStep() {
      this.setStep(this.step - 1);
      this.scrollBodyToTop();
    },
    async onClickNextStep() {
      const isValid = await this.validateAll();
      if (!isValid) return;

      this.setStep(this.step + 1);
      this.scrollBodyToTop();
    },
    scrollBodyToTop() {
      this.$nextTick(() =>
        this.$refs.body.scrollTo({ top: 0, behavior: 'smooth' })
      );
    },
    async onClickSubmit() {
      const isValid = await this.validateAll();
      if (!isValid) return;

      if (
        this.hasConfiguredMessageReward &&
        this.shortReviewConfig.policy === 'warn' &&
        this.validMessageLength < this.shortReviewConfig.length
      ) {
        const response = await this.windowService.confirm(
          this.$t('message_too_short_confirm', [this.shortReviewConfig.length])
        );
        if (!response) return;
      }

      const formData = this.generateFormData();
      this.isSubmitting = true;
      const promise = this.isEditing
        ? this.api.updateReview(this.review.id, formData)
        : this.api.createReview(this.product.id, this.subOrder?.id, formData);
      promise
        .then(() => {
          this.$emit('saved', {
            reward: this.isRewardEligible ? this.currentReward : 0
          });
          this.initFormValues(this.formValues);
        })
        .catch(error => {
          const isWriteIntervalRestricted = error.response?.data?.errors?.some(
            e => e.error === 'write_interval'
          );
          this.windowService.alert(
            isWriteIntervalRestricted
              ? this.$t('write_interval_restricted')
              : '리뷰 등록에 실패했습니다. 다시 시도해 주세요.'
          );
        })
        .finally(() => (this.isSubmitting = false));
    },
    generateFormData() {
      const formData = new FormData();
      formData.append('review[form_id]', this.reviewForm.id);
      let optionIdx = 0;
      this.formFields.forEach(field => {
        const value = this.formValues[field.key];
        if (field.key === 'media') {
          formData.append('review[images_attributes][]', '');
          formData.append('review[videos_attributes][]', '');
          value
            .filter(e => e.mediaStatus === 'ready')
            .forEach(item => {
              if (item.mediaType === 'image') {
                formData.append('review[images_attributes][][url]', item.url);
                formData.append('review[images_attributes][][file]', item.file);
                formData.append(
                  'review[images_attributes][][ai_clothing_detected]',
                  item.aiClothingDetected
                );
              } else if (item.mediaType === 'video')
                formData.append('review[videos_attributes][][id]', item.id);
            });
        } else if (field.option_type) {
          if (value) {
            const option = this.review.options.find(
              e => e.review_option_type_id === field.option_type.id
            );
            if (option?.id)
              formData.append(
                `review[options_attributes][${optionIdx}][id]`,
                option.id
              );

            formData.append(
              `review[options_attributes][${optionIdx}][review_option_type_id]`,
              field.option_type.id
            );
            formData.append(
              `review[options_attributes][${optionIdx}][value]`,
              value
            );
            optionIdx += 1;
          }
        } else formData.append(`review[${field.key}]`, value ?? '');
      });

      if (!this.isLoggedIn && this.isEditing)
        formData.append('password', this.$route.query.password);

      if (this.formValues.matched_ai_keywords) {
        this.formValues.matched_ai_keywords.forEach(keyword => {
          formData.append('review[matched_ai_keywords][]', keyword);
        });
      }

      this.checkedConsentIds.forEach(id => {
        formData.append('review[consent_preset_ids][]', id);
      });

      if (this.$route.query.review_source) {
        formData.append(
          'review[review_source]',
          this.$route.query.review_source
        );
      }

      if (this.$route.query.nth_step) {
        formData.append('nth_step', this.$route.query.nth_step);
      }

      return formData;
    },
    onClickRewardSummary() {
      this.openBottomSheet({
        component: RewardDialog,
        props: {
          title: this.$t('total_reward_title'),
          rows: this.rewardItems,
          tableCaption: this.$t('reward_summary_description'),
          missingRequiredFields: this.missingRequiredFields
        }
      });
    }
  }
};
